import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { PawPrint, ArrowRight, Truck, PackagePlus, Building2, Utensils } from "lucide-react";

/**
 * PetFoodCallout — distinct, warm-accent card highlighting pet-food flows.
 * `variant="donate"` — for /ways-to-give (donors)
 * `variant="receive"` — for /resources (rescues)
 */
export default function PetFoodCallout({ variant = "donate" }) {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/food/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);

    const isDonate = variant === "donate";
    const to = "/pet-food";
    const cta = isDonate ? "Donate Food" : "Request Food";

    return (
        <Link to={to} data-testid={`pet-food-callout-${variant}`} className="block group">
            <div
                className={`relative overflow-hidden rounded-2xl p-6 lg:p-8 border-2 transition-all hover:-translate-y-0.5 hover:shadow-xl ${
                    isDonate
                        ? "bg-gradient-to-br from-[#f9d9b8] via-[#f7c69a] to-[#ea9a67] border-[#c2724e] text-[#3a2718]"
                        : "bg-gradient-to-br from-[#fff2e1] via-[#fce0bd] to-[#f2b787] border-[#b8804d] text-[#2e2015]"
                }`}
            >
                {/* Paw texture */}
                <PawPrint className="absolute -right-3 -top-3 w-32 h-32 opacity-15 rotate-12 group-hover:rotate-6 transition-transform" />
                <PawPrint className="absolute right-16 bottom-2 w-16 h-16 opacity-10 -rotate-12" />

                <div className="relative grid lg:grid-cols-[auto_1fr_auto] gap-5 items-center">
                    <div className="w-14 h-14 rounded-full bg-white/70 backdrop-blur-sm flex items-center justify-center shadow-sm shrink-0">
                        <PawPrint className="w-7 h-7 text-[#8b4a1c]" fill="currentColor" strokeWidth={0} />
                    </div>

                    <div className="min-w-0">
                        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-[#8b4a1c] mb-1">
                            <Utensils className="w-3.5 h-3.5" /> Pet Food · {isDonate ? "Donate" : "Receive"}
                        </div>
                        <h3 className="font-serif text-2xl lg:text-3xl leading-[1.1]">
                            {isDonate ? (
                                <>Fill a bowl.<br /><em className="not-italic">Save a rescue&apos;s budget.</em></>
                            ) : (
                                <>Kibble low?<br /><em className="not-italic">The food network has your back.</em></>
                            )}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed max-w-xl opacity-90">
                            {isDonate
                                ? "Drop-off, ship, or coordinate a bulk donation. Individuals, businesses, and pet-food brands all welcome — every pound is matched to a rescue in need."
                                : "Tap into food banks, brand donation programs, community drives, and emergency assistance. Post a food ask and the network responds in hours."}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                            {isDonate ? (
                                <>
                                    <Chip icon={PackagePlus} label="Drop-off" />
                                    <Chip icon={Truck} label="Ship" />
                                    <Chip icon={Building2} label="Brand partnership" />
                                    <Chip icon={PawPrint} label="Bulk pickup" />
                                </>
                            ) : (
                                <>
                                    <Chip label="Food banks" />
                                    <Chip label="Brand programs" />
                                    <Chip label="Community drives" />
                                    <Chip label="Emergency assist" />
                                </>
                            )}
                        </div>
                        {stats && (
                            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-[11px] uppercase tracking-widest opacity-80">
                                <span><strong className="font-serif text-lg not-italic normal-case tracking-normal">{stats.pounds_pledged.toLocaleString()}</strong> lbs pledged</span>
                                <span><strong className="font-serif text-lg not-italic normal-case tracking-normal">{stats.pounds_needed.toLocaleString()}</strong> lbs needed</span>
                                {stats.urgent_open > 0 && (
                                    <span className="text-[#7a1c1c]"><strong className="font-serif text-lg not-italic normal-case tracking-normal">{stats.urgent_open}</strong> urgent ask{stats.urgent_open === 1 ? "" : "s"}</span>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="shrink-0 flex flex-col items-stretch gap-2 lg:min-w-[180px]">
                        <span
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#8b4a1c] text-white h-11 px-5 text-sm font-semibold shadow-sm group-hover:bg-[#6f3811] transition-colors"
                            data-testid={`pet-food-cta-${variant}`}
                        >
                            {cta} <ArrowRight className="w-4 h-4" />
                        </span>
                        <span className="text-[11px] text-center opacity-75">
                            {isDonate ? "Individual · Business · Brand" : "Free · Open to all partner rescues"}
                        </span>
                    </div>
                </div>
            </div>
        </Link>
    );
}

function Chip({ icon: Icon, label }) {
    return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/60 border border-white/70 text-[#5b3411]">
            {Icon && <Icon className="w-3 h-3" />} {label}
        </span>
    );
}
